import { normalizeName } from '../shared/naming';
import type {
  PieceNameAliasId,
  PiecePositionId,
  ProjectId,
  UnitTypeId,
  UserId,
} from '../shared/ids';
import { nomeDaPeca } from './planilha-por-peca';

/**
 * As peças da planilha na planta (Fase 28, etapa 3 — RN-427 a RN-430).
 *
 * A peça importada (B92, E06-A - R40, AP24) já se executa pelo nome, sem desenho (RN-411). O
 * que faltava era **vê-la** na planta: onde fica o B92? Aqui ela ganha um ponto — clicado na
 * planta, na ferramenta do tipo dela —, e só isso. A quantidade continua a da planilha: o ponto
 * identifica, não mede (RN-427). Desenhar o contorno do bloco continua possível, e é outra coisa.
 */

/**
 * O tipo da peça importada, que decide em que ferramenta da planta ela aparece (RN-428).
 *
 * A planilha não diz o tipo; a disciplina dos serviços dela diz: "BLOCO" é bloco, "ESTACAS" é
 * estaca, "ARRANQUE DO PILAR" é arranque. A peça que não se encaixa em nenhuma é `OUTRA`, e
 * não aparece em ferramenta nenhuma — mas continua executável pelo nome.
 */
export type ImportedPieceKind =
  'ESTACA' | 'BLOCO' | 'BALDRAME' | 'ARRANQUE' | 'MURO' | 'PILAR_CONTENCAO' | 'OUTRA';

export const IMPORTED_PIECE_KIND_LABEL: Readonly<Record<ImportedPieceKind, string>> = {
  ESTACA: 'Estacas',
  BLOCO: 'Blocos',
  BALDRAME: 'Baldrames',
  ARRANQUE: 'Arranques',
  MURO: 'Muros',
  PILAR_CONTENCAO: 'Pilares de contenção',
  OUTRA: 'Outras peças',
};

/** O tipo pela disciplina dos serviços da peça — a primeira que se reconhece vale (RN-428). */
export function importedPieceKind(disciplineNames: readonly string[]): ImportedPieceKind {
  const nomes = disciplineNames.map(normalizeName);
  const tem = (...partes: readonly string[]): boolean =>
    nomes.some((nome) => partes.every((parte) => nome.includes(parte)));
  if (tem('arranque')) return 'ARRANQUE';
  if (tem('estac')) return 'ESTACA';
  if (tem('baldrame')) return 'BALDRAME';
  if (tem('bloco')) return 'BLOCO';
  if (tem('pilar', 'conten')) return 'PILAR_CONTENCAO';
  if (tem('muro') || tem('cortina')) return 'MURO';
  return 'OUTRA';
}

/** O ponto da peça na planta: metros, nas coordenadas da tipologia (como o centro da estaca). */
export interface PiecePosition {
  readonly id: PiecePositionId;
  readonly projectId: ProjectId;
  readonly unitTypeId: UnitTypeId;
  /** Como a planilha o escreve. A chave é o nome sem caixa e sem espaço nas pontas. */
  readonly pieceName: string;
  readonly x: number;
  readonly y: number;
  readonly updatedAt: string;
  readonly updatedByUserId?: UserId;
}

/** Uma por tipologia × nome — a mesma chave do índice da `0072` e da marca de execução. */
export function piecePositionKey(unitTypeId: string, pieceName: string): string {
  return `${unitTypeId}|${pieceName.trim().toLocaleLowerCase('pt-BR')}`;
}

/**
 * Quanto da peça já foi executado (RN-430): nenhum serviço, alguns, ou todos os que a planilha
 * dá para ela. É o que pinta a peça na planta.
 */
export type ImportedPieceStatus = 'NAO_INICIADA' | 'EM_EXECUCAO' | 'EXECUTADA';

export function importedPieceStatus(services: number, executed: number): ImportedPieceStatus {
  if (executed <= 0) return 'NAO_INICIADA';
  return executed >= services ? 'EXECUTADA' : 'EM_EXECUCAO';
}

/** Uma peça importada da tipologia, pronta para a lista e para a planta. */
export interface ImportedPieceSummary {
  readonly name: string;
  readonly kind: ImportedPieceKind;
  readonly position?: { readonly x: number; readonly y: number };
  /** Quantos serviços a planilha dá para a peça. */
  readonly services: number;
  /** Quantos deles já têm marca de execução, em qualquer dia. */
  readonly executedServices: number;
  /** O dia da execução mais recente, `AAAA-MM-DD`. */
  readonly lastExecutedOn?: string;
  readonly status: ImportedPieceStatus;
}

/**
 * As peças importadas de uma tipologia, juntas pelo nome (RN-427 a RN-430), na ordem do nome.
 *
 * - `quantities`: as linhas da planilha da tipologia (qualquer serviço, com ou sem item);
 * - `disciplineOf`: a disciplina de cada serviço — é dela que sai o tipo;
 * - `positions`: os pontos já clicados;
 * - `executions`: as marcas vivas de execução da tipologia, por nome.
 */
export function summarizeImportedPieces(input: {
  readonly quantities: readonly { readonly pieceName: string; readonly serviceId: string }[];
  readonly disciplineOf: (serviceId: string) => string | undefined;
  readonly positions: readonly PiecePosition[];
  readonly executions: readonly {
    readonly pieceName: string;
    readonly serviceId: string;
    readonly date: string;
  }[];
}): readonly ImportedPieceSummary[] {
  const chave = (nome: string): string => nome.trim().toLocaleLowerCase('pt-BR');

  const pecas = new Map<string, { nome: string; servicos: Set<string> }>();
  for (const linha of input.quantities) {
    const k = chave(linha.pieceName);
    const peca = pecas.get(k) ?? { nome: linha.pieceName.trim(), servicos: new Set<string>() };
    peca.servicos.add(linha.serviceId);
    pecas.set(k, peca);
  }

  const posicoes = new Map(input.positions.map((posicao) => [chave(posicao.pieceName), posicao]));
  const executadas = new Map<string, { servicos: Set<string>; ultima: string }>();
  for (const marca of input.executions) {
    const k = chave(marca.pieceName);
    const atual = executadas.get(k) ?? { servicos: new Set<string>(), ultima: marca.date };
    atual.servicos.add(marca.serviceId);
    if (marca.date > atual.ultima) atual.ultima = marca.date;
    executadas.set(k, atual);
  }

  return [...pecas.entries()]
    .map(([k, peca]): ImportedPieceSummary => {
      const disciplinas = [...peca.servicos].flatMap((servico) => {
        const nome = input.disciplineOf(servico);
        return nome === undefined ? [] : [nome];
      });
      const posicao = posicoes.get(k);
      const execucao = executadas.get(k);
      // Só conta o serviço que a planilha dá para a peça: marca de outro serviço não a completa.
      const feitos =
        execucao === undefined
          ? 0
          : [...execucao.servicos].filter((servico) => peca.servicos.has(servico)).length;
      return {
        name: peca.nome,
        kind: importedPieceKind(disciplinas),
        ...(posicao === undefined ? {} : { position: { x: posicao.x, y: posicao.y } }),
        services: peca.servicos.size,
        executedServices: feitos,
        ...(execucao === undefined ? {} : { lastExecutedOn: execucao.ultima }),
        status: importedPieceStatus(peca.servicos.size, feitos),
      };
    })
    .sort((a, b) => a.name.localeCompare(b.name, 'pt-BR', { numeric: true }));
}

// ── O nome novo da peça (20/10/2026 — RN-457 a RN-460) ──────────────────────────

/**
 * O nome que a planilha por peça dá a uma peça renomeada, e o nome dela hoje (`0081`).
 *
 * O elemento muda de nome no projeto depois de confirmado o quantitativo, e o nome novo
 * substitui o antigo na peça inteira (RN-457). A planilha, porém, continua escrevendo o antigo:
 * é por esta lembrança que a reimportação reconhece a peça (RN-458).
 */
export interface PieceNameAlias {
  readonly id: PieceNameAliasId;
  readonly projectId: ProjectId;
  readonly unitTypeId: UnitTypeId;
  /** Como a planilha escreve a peça. */
  readonly sheetName: string;
  /** Como a peça se chama hoje. */
  readonly pieceName: string;
  readonly updatedAt: string;
  readonly updatedByUserId?: UserId;
}

/** O que muda na lembrança dos nomes de uma tipologia quando uma peça é renomeada. */
export interface PieceNameAliasChange {
  /** As que ficam com o destino novo (com o id, a que já existia) e a que entra. */
  readonly save: readonly {
    readonly id?: PieceNameAliasId;
    readonly sheetName: string;
    readonly pieceName: string;
  }[];
  /** As que já não dizem nada: o nome da planilha voltou a ser o da peça. */
  readonly remove: readonly PieceNameAliasId[];
}

/**
 * A lembrança depois de `de` passar a se chamar `para` (RN-458).
 *
 * - quem apontava para `de` passa a apontar para `para` — renomear duas vezes (B06 → B06A →
 *   B06B) não perde o nome da planilha;
 * - a que volta ao próprio nome (B06A → B06, com a lembrança B06 → B06A) sai;
 * - `de` também fica lembrado, para a planilha que já vinha com ele — salvo quando ele já é o
 *   nome de planilha de outra peça (a troca de nomes entre duas peças).
 */
export function renamePieceAliases(
  aliases: readonly Pick<PieceNameAlias, 'id' | 'sheetName' | 'pieceName'>[],
  de: string,
  para: string,
): PieceNameAliasChange {
  const antigo = nomeDaPeca(de);
  const novo = nomeDaPeca(para);
  const save: PieceNameAliasChange['save'][number][] = [];
  const remove: PieceNameAliasId[] = [];
  for (const alias of aliases) {
    if (nomeDaPeca(alias.pieceName) !== antigo) continue;
    if (nomeDaPeca(alias.sheetName) === novo) remove.push(alias.id);
    else save.push({ id: alias.id, sheetName: alias.sheetName, pieceName: para });
  }
  const jaLembrado = aliases.some((alias) => nomeDaPeca(alias.sheetName) === antigo);
  if (!jaLembrado && antigo !== novo) save.push({ sheetName: de.trim(), pieceName: para });
  return { save, remove };
}

/** A lembrança como a reimportação a usa: nome da planilha (comparável) → nome de hoje. */
export function pieceAliasMap(
  aliases: readonly Pick<PieceNameAlias, 'sheetName' | 'pieceName'>[],
): Readonly<Record<string, string>> {
  return Object.fromEntries(
    aliases.map((alias) => [nomeDaPeca(alias.sheetName), alias.pieceName] as const),
  );
}
